<template>
  <div class="right-panel">
    <div class="row">
      <div class="col">
        <h4>{{ terms?.ParticipationRegionContactPlural }}</h4>
      </div>
      <div class="col text-right">
        <button class="btn btn-primary" @click="createNewContact">
          Create New Contact
        </button>
      </div>
    </div>

    <div class="d-flex flex-row content form">
      <div class="row mr-1 form-input">
        <label for="basic-url">Search by {{ settings.stateName }}</label>
        <div class="input-group">
          <select class="form-select" id="venue" v-model.number="regionId" @change="onRegionChange">
            <option :value="0" selected>-- All --</option>
            <option v-for="item in regions" :value="item.regionId">
              {{ item.regionName }}
            </option>
          </select>
        </div>
      </div>
      <div class="row mr-1 form-input">
        <label for="basic-url">Search by Name</label>
        <div class="input-group">
          <input type="text" class="form-control" placeholder="Enter first or last name"
            @input="searchByNameDebounce" />
        </div>
      </div>
      <div class="row form-input">
        <label for="basic-url">Search by Email</label>
        <div class="input-group">
          <input type="text" class="form-control" placeholder="email@domain.com" @input="searchByEmailDebounce" />
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <EasyDataTable must-sort table-class-name="datatable" :headers="headers" :items="items" buttons-pagination
        :alternating="true" class="data-table">
        <template #item-firstName="item">
          {{ item.firstName }}
        </template>
        <template #item-lastName="item">
          {{ item.lastName }}
        </template>
        <template #item-regionName="item">
          {{ item.regionName }}
        </template>
        <template #item-jobTitle="item">
          {{ item.jobTitle }}
        </template>
        <template #item-email="item">
          {{ item.email }}
        </template>
        <template #item-createDate="item">
          {{ moment(item.createDate).format("DD MMM YYYY") }}
        </template>
        <template #item-actions="item">
          <div class="icon-col" style="min-width: 80px">
            <button class="btn-edit" @click="() => openStateContactModal(item)" title="Edit">
              <i class="bi bi-pencil-square"></i>
            </button>
            <button class="btn-primary" @click="() => openDeleteStateContactModal(item)">
              <i class="bi bi-trash ms-1"></i>
            </button>
          </div>
        </template>
        <template #empty-message>
          <span>This member currently has no notes.</span>
        </template>
      </EasyDataTable>
    </div>

    <Modal :title="modalTitle" ref="stateContactModal">
      <template #body>
        <div class="container">
          <div class="row mb-3">
            <div class="col col-12 col-md-6 form-input">
              <label for="basic-url">First Name</label>
              <div class="input-group">
                <input type="text" class="form-control" placeholder="Enter first name" aria-label="Enter first name"
                  v-model="fieldsToEdit.firstName" />
                <span class="error" v-if="v$.firstName.$error">
                  {{ 'First Name is required' }}
                </span>
              </div>
            </div>
            <div class="col col-12 col-md-6 form-input">
              <label for="basic-url">Last Name</label>
              <div class="input-group">
                <input type="text" class="form-control" placeholder="Enter last name" aria-label="Enter last name"
                  v-model="fieldsToEdit.lastName" />
                <span class="error" v-if="v$.lastName.$error">
                  {{ 'Last Name is required' }}
                </span>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col col-12 col-md-6 form-input">
              <label for="basic-url">Email</label>
              <div class="input-group">
                <input type="text" class="form-control" placeholder="email@domain.com" aria-label="email@domain.com"
                  v-model="fieldsToEdit.email" />
                <span class="error" v-if="v$.email.$error">
                  {{ 'Email is required or Invalid' }}
                </span>
              </div>
            </div>
            <div class="col col-12 col-md-6 form-input">
              <label for="basic-url">{{ settings.stateName }}</label>
              <div class="input-group">
                <select class="form-select" v-model.number="fieldsToEdit.regionId" @change="regionChangeOnModal">
                  <option :value="0" selected>-- Select --</option>
                  <option v-for="item in regions" :value="item.regionId">
                    {{ item.regionName }}
                  </option>
                </select>
                <span class="error" v-if="v$.regionId.$error">
                  {{ settings.stateName }} is required
                </span>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col col-12 col-md-6 form-input">
              <label for="basic-url">Job Title</label>
              <input type="text" class="form-control" aria-label="email@domain.com" v-model="fieldsToEdit.jobTitle" />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="btn btn-primary" :disabled="v$.$errors.length > 0" @click="saveStateContact">
          Submit
        </button>
      </template>
    </Modal>

    <Modal title="Remove Contact" ref="removeStateContactModal">
      <template #body>Are you sure you want to remove
        <strong>{{ `${selectedStateContact?.firstName + ' ' + selectedStateContact?.lastName}` }}</strong>?</template>
      <template #footer>
        <button class="btn btn-danger" @click="removeStateContact">Remove</button>
      </template>
    </Modal>

    <Notification ref="removeMsg" colorScheme="text-bg-success">
      <template #body>Successfully removed
        <strong>{{ `${selectedStateContact?.firstName + ' ' + selectedStateContact?.lastName}` }}</strong></template>
    </Notification>

    <Notification ref="saveMsg" colorScheme="text-bg-success">
      <template #body>Successfully saved!</template>
    </Notification>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import type { Header, Item } from "vue3-easy-data-table";
import moment from 'moment';
import Modal from '@/components/common/Modal.vue';
import Notification from '@/components/common/Notification.vue'
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { type IStateContactRequest } from "@/types/IStateContact";
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { useStateContactStore } from '@/stores/state-contact-store';
import { useSettingStore } from "@/stores/setting-store";
import { useTermsStore } from "@/stores/language-terms";
const { terms } = storeToRefs(useTermsStore());

const { loadRegions, loadStateContacts, postStateContact, updateStateContactById, removeStateContactById } = useStateContactStore();
const { regions, stateContacts } = storeToRefs(useStateContactStore())

const regionId = ref(0);  //set 0(All) as default value
const searchByName = ref('');
const searchByEmail = ref('');
const modalTitle = ref();
const fieldsToEdit = ref({} as IStateContactRequest);

const { getSetting } = useSettingStore();
const { setting } = storeToRefs(useSettingStore());
const settings = ref(setting);

const items = ref<Item[]>([]);
const headers: Header[] = [
  { text: "First Name", value: "firstName", sortable: true, width: 150 },
  { text: "Last Name", value: "lastName", sortable: true, width: 150 },
  { text: (settings ? settings?.value.stateName : 'State'), value: "regionName", sortable: true, width: 200 },
  { text: "Title", value: "jobTitle", sortable: true },
  { text: "Email", value: "email", sortable: true, width: 150 },
  { text: "Date Created", value: "createDate", sortable: true },
  { text: "", value: "actions", width: 80 },
];

onMounted(async () => {
  await loadRegions();
  await refreshData();
  await getSetting()
})

const refreshData = async () => {
  await loadStateContacts(searchByName.value, searchByEmail.value, regionId?.value);
  if (stateContacts.value?.length > 0) {
    items.value = stateContacts.value;
  }
  else {
    items.value = [];
  }
}

const rules = computed(() => ({
  firstName: { required },
  lastName: { required },
  email: { required, email },
  regionId: { required },
  jobTitle: { required },
}))

const v$ = useVuelidate(rules, fieldsToEdit);

let stateContactModal = ref<any | null>(null);
const createNewContact = () => {
  v$.value.$reset();
  modalTitle.value = "Create New Contact";
  fieldsToEdit.value = {} as IStateContactRequest;
  fieldsToEdit.value.regionId = 0;  //set 0(All) as default value
  if (stateContactModal.value) {
    stateContactModal.value.show();
  }
}

const onRegionChange = (event: any) => {
  regionId.value = event.target.value;
  if (event.target.value == '-- All --') {
    regionId.value = 0;
  }
}
const regionChangeOnModal = (event: any) => {
  fieldsToEdit.value.regionId = event.target.value;
  if (event.target.value == '-- Select --') {
    fieldsToEdit.value.regionId = 0;
  }
}

const searchByNameDebounce = debounce(($event) => {
  if ($event.target.value && $event.target.value.length <= 2) return
  searchByName.value = $event.target.value;
}, 500)

const searchByEmailDebounce = debounce(($event) => {
  if ($event.target.value && $event.target.value.length <= 2) return
  searchByEmail.value = $event.target.value;
}, 500)

watch([searchByName, searchByEmail, regionId], async () => {
  await refreshData();
})

const openStateContactModal = (item: any) => {
  v$.value.$reset();
  modalTitle.value = "Edit Contact";
  fieldsToEdit.value = Object.assign({}, JSON.parse(JSON.stringify(item)) as IStateContactRequest);
  if (stateContactModal.value) {
    stateContactModal.value.show();
  }
};

const selectedStateContact = ref();
let removeStateContactModal = ref<any | null>(null);
const openDeleteStateContactModal = (item: any) => {
  selectedStateContact.value = Object.assign({}, item)
  if (removeStateContactModal.value) {
    removeStateContactModal.value.show()
  }
};

let removeMsg = ref<any | null>(null);
const removeStateContact = async () => {
  result = await removeStateContactById(selectedStateContact.value.regionContactId);
  if (result > 0) {
    await refreshData();
    removeStateContactModal.value.hide()
    removeMsg?.value?.show()
  }
}

let saveMsg = ref<any | null>(null);
let result = 0;
const saveStateContact = async () => {
  v$.value.$reset();
  const isValid = await v$.value.$validate();
  if (isValid) {
    try {
      if (fieldsToEdit.value.regionContactId) {
        result = await updateStateContactById(fieldsToEdit.value)
      } else {
        result = await postStateContact(fieldsToEdit.value)
      }
      if (result > 0) {
        await refreshData();
        stateContactModal.value.hide()
        saveMsg?.value?.show();
      }
    } catch (error) {
      console.log(error)
    }
  }
}

</script>

<style scoped>
.mr-1 {
  margin-right: 10px !important;
}

label.form-label {
  font-size: 13px;
}

.delivererModal .form-input label {
  padding-bottom: 5px;
  margin-bottom: 0;
}

.delivererModal .form-input {
  margin-bottom: 10px;
}

.text-right {
  text-align: right;
}

.error {
  display: block;
  width: 100%;
  color: red;
  font-size: 14px;
  margin-top: 5px;
}
</style>
